import { getAssetPath } from '@stencil/core/internal/client';
export { getAssetPath, render, setAssetPath, setNonce, setPlatformOptions } from '@stencil/core/internal/client';

let CACHED_MAP;
const getIconMap = () => {
    if (typeof window === 'undefined') {
        return new Map();
    }
    else {
        if (!CACHED_MAP) {
            const win = window;
            win.Bsdsicons = win.Bsdsicons || {};
            CACHED_MAP = win.Bsdsicons.map = win.Bsdsicons.map || new Map();
        }
        return CACHED_MAP;
    }
};
const addIcons = (icons) => {
    Object.keys(icons).forEach(name => {
        addToIconMap(name, icons[name]);
        /**
         * Os desenvolvedores também podem passar o objeto SVG diretamente
         * e @bsds-lib-component/icons podem mapear o objeto para um nome de caso kebab.
         * Exemplo: addIcons({ addCircleOutline });
         * Isso criará uma entrada "addCircleOutline" e
         * uma entrada "add-circle-outline".
         * Uso: <bsds-icon name="add-circle-outline"></bsds-icon>
         * Usar name="addCircleOutline" também é válido, mas a
         * nomenclatura de caso kebab é preferida.
         */
        const toKebabCase = name.replace(/([a-z0-9]|(?=[A-Z]))([A-Z0-9])/g, "$1-$2").toLowerCase();
        if (name !== toKebabCase) {
            addToIconMap(toKebabCase, icons[name]);
        }
    });
};
const addToIconMap = (name, data) => {
    const map = getIconMap();
    const existingIcon = map.get(name);
    if (existingIcon === undefined) {
        map.set(name, data);
        /**
         * Importar e definir a mesma referência de ícone
         * várias vezes não deve gerar um aviso.
         */
        if (existingIcon === undefined) {
            map.set(name, data);
        }
        else if (existingIcon !== data) {
            console.warn(`[@bsds-lib-component/icons aviso]: Vários ícones foram mapeados para nomear "${name}". Garanta que vários ícones não sejam mapeados para o mesmo nome de ícone.`);
        }
    }
};
const getUrl = (i) => {
    let url = getName(i.name, i.icon);
    if (url) {
        return getNamedUrl(url, i);
    }
    if (i.icon) {
        url = getSrc(i.icon);
        if (url) {
            return url;
        }
    }
    return null;
};
const getNamedUrl = (iconName, iconEl) => {
    const url = getIconMap().get(iconName);
    if (url) {
        return url;
    }
    try {
        return getAssetPath(`svg/${iconName}.svg`);
    }
    catch (e) {
        /**
         * Na versão de construção de elementos personalizados do @bsds-lib-component/icons, referenciar um ícone
         * pelo nome lançará um erro de URL inválido porque o caminho do ativo não está definido.
         * Isso captura esse erro e registra algo que é mais amigável ao desenvolvedor.
         * Também incluímos uma referência ao elemento bsds-icon para que os desenvolvedores possam
         * descobrir qual instância do bsds-icon precisa ser atualizada.
         */
        console.warn(`[@bsds-lib-component/icons aviso]: Não foi possível carregar o ícone com o nome "${iconName}". Certifique-se de que o ícone esteja registrado usando addIcons ou que os dados SVG do ícone sejam passados diretamente para o componente do ícone.`, iconEl);
    }
};
const getName = (iconName, icon) => {
    if (!iconName && icon && !isSrc(icon)) {
        iconName = icon;
    }
    if (isStr(iconName)) {
        iconName = toLower(iconName);
    }
    if (!isStr(iconName) || iconName.trim() === '') {
        return null;
    }
    // permitir apenas caracteres alfabéticos e traços
    const invalidChars = iconName.replace(/[a-z]|-|\d/gi, '');
    if (invalidChars !== '') {
        return null;
    }
    return iconName;
};
const getSrc = (src) => {
    if (isStr(src)) {
        src = src.trim();
        if (isSrc(src)) {
            return src;
        }
    }
    return null;
};
const isSrc = (str) => str.length > 0 && /(\/|\.)/.test(str);
const isStr = (val) => typeof val === 'string';
const toLower = (val) => val.toLowerCase();
/**
 * Elementos dentro de componentes da web às vezes precisam herdar atributos globais
 * definidos no host. Por exemplo, a entrada interna em `bsds-input` deve herdar
 * o atributo `title` que os desenvolvedores definem diretamente em `bsds-input`. Esta
 * função auxiliar deve ser chamada em componentWillLoad e atribuída a uma variável
 * que é usada posteriormente na função render.
 *
 * Isso não precisa ser reativo, pois alterar atributos no elemento host
 * não aciona uma nova renderização.
 */
const inheritAttributes = (el, attributes = []) => {
    const attributeObject = {};
    attributes.forEach(attr => {
        if (el.hasAttribute(attr)) {
            const value = el.getAttribute(attr);
            if (value !== null) {
                attributeObject[attr] = el.getAttribute(attr);
            }
            el.removeAttribute(attr);
        }
    });
    return attributeObject;
};

export { inheritAttributes as a, addIcons, getUrl as g, isStr as i };
